import { Switch as BaseSwitch } from "@base-ui/react/switch";
import type { FunctionComponent } from "react";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

export interface SwitchProps extends Omit<
  BaseSwitch.Root.Props,
  "className" | "onChange" | "onCheckedChange" | "render"
> {
  /** antd-style change handler — receives the new checked state (Form.Item / Controller compatible). */
  onChange?: (checked: boolean) => void;
  size?: "small" | "default";
  className?: string;
}

/** Toggle switch; `checked` + `onChange(checked)` like the antd Switch it replaces. */
export const Switch: FunctionComponent<SwitchProps> = ({ onChange, size = "default", className, ...props }) => {
  const small = size === "small";
  // A present-but-undefined `checked` (an empty RHF field) means controlled-off, not "switch to uncontrolled".
  const checkedProps = "checked" in props ? { checked: props.checked ?? false } : {};

  return (
    <BaseSwitch.Root
      onCheckedChange={(checked) => onChange?.(checked)}
      className={cn(
        "relative inline-flex shrink-0 cursor-pointer items-center rounded-full align-middle transition-colors",
        // Off track = the 3:1 control outline level; on track = primary, thumb = text-on-primary.
        "bg-noa-border-input hover:bg-noa-text-muted",
        "data-[checked]:bg-noa-primary data-[checked]:hover:bg-noa-primary-hover",
        FOCUS_RING,
        "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-65",
        small ? "h-4 w-7 min-w-7" : "h-[22px] w-11 min-w-11",
        className
      )}
      {...props}
      {...checkedProps}
    >
      <BaseSwitch.Thumb
        className={cn(
          "absolute left-0.5 rounded-full bg-noa-text-on-primary shadow-sm shadow-noa-shadow transition-transform",
          small ? "size-3 data-[checked]:translate-x-3" : "size-[18px] data-[checked]:translate-x-[22px]"
        )}
      />
    </BaseSwitch.Root>
  );
};
